import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import type { LocationRow } from "@garden/types/db";
import { supabase } from "../auth";
import { createLocation } from "../db/locations";
import { parseGeoJsonForBed, GeoJsonParseError } from "../lib/geojson";
import "./LocationCreateModal.css";

interface GeoJsonImportModalProps {
  gardenId: string;
  viewport: { width: number; height: number };
  onClose: () => void;
  onCreated: (loc: LocationRow) => void;
}

export function GeoJsonImportModal({
  gardenId,
  viewport,
  onClose,
  onCreated,
}: GeoJsonImportModalProps) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const t = await file.text();
      setText(t);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read file.");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError("Supabase not configured.");
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!text.trim()) {
      setError("Paste GeoJSON or pick a file.");
      return;
    }

    let parsed: ReturnType<typeof parseGeoJsonForBed>;
    try {
      parsed = parseGeoJsonForBed(text, viewport);
    } catch (err) {
      setError(err instanceof GeoJsonParseError ? err.message : "Could not parse GeoJSON.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const loc = await createLocation(supabase, {
        garden_id: gardenId,
        name: name.trim(),
        kind: "bed",
        polygon: parsed.polygon,
        polygon_source_geojson: parsed.source,
      });
      onCreated(loc);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create bed.");
      setSubmitting(false);
    }
  };

  return (
    <div className="Modal__backdrop" onClick={onClose} role="presentation">
      <div
        className="Modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gj-modal-title"
      >
        <h2 id="gj-modal-title" className="Modal__title">
          Import a bed from GeoJSON
        </h2>
        <p className="Modal__subtitle">
          Draw a polygon at <a href="https://geojson.io/" target="_blank" rel="noreferrer">geojson.io</a>{" "}
          and paste the JSON below. The polygon will be scale-fitted to the centre
          of your garden.
        </p>

        <form onSubmit={handleSubmit} className="Modal__form">
          <label className="Modal__field">
            <span>Bed name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="South bed"
            />
          </label>

          <label className="Modal__field">
            <span>GeoJSON file <em>optional</em></span>
            <input type="file" accept=".geojson,.json,application/geo+json,application/json" onChange={handleFile} />
          </label>

          <label className="Modal__field">
            <span>or paste GeoJSON</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder='{ "type": "Feature", "geometry": { "type": "Polygon", "coordinates": [[[…]]] } }'
              spellCheck={false}
              style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}
            />
          </label>

          {error && <p className="Modal__error">{error}</p>}

          <div className="Modal__actions">
            <button
              type="button"
              className="PlantForm__btn PlantForm__btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="PlantForm__btn PlantForm__btn--primary"
              disabled={submitting}
            >
              {submitting ? "Importing…" : "Import bed"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
